/**
 * SpeciesEditModal Component
 *
 * Full-featured modal for creating/editing species with 4 tabs:
 * 1. Basic Info - Core species data
 * 2. Regional Presence - State-level presence/abundance
 * 3. Water Types - Water body types (fish only)
 * 4. Media - Image upload/URL management
 *
 * Epic: Super Admin Catalog Management Console
 * Story: SCRUM-300
 */

import { useState, useEffect, useCallback } from 'react';
import { X, Save as _Save, Loader, AlertCircle as _AlertCircle, Plus, Trash, Edit, Fish, Upload, Image as ImageIcon, Save, AlertCircle } from 'lucide-react';
import { speciesCatalogAdminApi } from '../../services/catalogAdminApi';
import FormField from './FormField';
import ConfirmModal from '../shared/ConfirmModal';

// US States for regional presence
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Category options by species type
const CATEGORY_OPTIONS = {
  fish: ['Coldwater', 'Warmwater', 'Saltwater', 'Anadromous', 'Catadromous', 'Brackish'],
  game: ['Big Game', 'Small Game', 'Upland Birds', 'Waterfowl', 'Predators', 'Furbearers'],
  wildlife: ['Birds', 'Mammals', 'Reptiles', 'Amphibians', 'Insects', 'Marine']
};

export default function SpeciesEditModal({ species, isCreating, onSave, onClose }) {
  // Tab state
  const [activeTab, setActiveTab] = useState('basic');

  // Form state
  const [formData, setFormData] = useState({
    common_name: '',
    scientific_name: '',
    species_type: 'fish',
    category: '',
    default_description: '',
    typical_size_min: '',
    typical_size_max: '',
    size_unit: 'inches',
    search_keywords: [],
    popularity_score: 0,
    image_url: '',
    thumbnail_url: '',
    is_active: true
  });

  // Regional presence state
  const [regionalPresence, setRegionalPresence] = useState([]);
  const [editingPresence, setEditingPresence] = useState(null);
  const [presenceForm, setPresenceForm] = useState({});

  // Water types state
  const [waterTypes, setWaterTypes] = useState([]);
  const [addingWaterType, setAddingWaterType] = useState(false);
  const [waterTypeForm, setWaterTypeForm] = useState({ water_type: '', water_body_type: '' });

  // Image upload state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, action: null, data: null, message: '' });
  const [errors, setErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  /**
   * Initialize form with species data
   */
  useEffect(() => {
    if (species && !isCreating) {
      const speciesType = species.species_type || 'fish';
      setFormData({
        common_name: species.common_name || '',
        scientific_name: species.scientific_name || '',
        species_type: speciesType,
        category: species.category || '',
        default_description: species.default_description || '',
        typical_size_min: species.typical_size_min || '',
        typical_size_max: species.typical_size_max || '',
        size_unit: species.size_unit || 'inches',
        search_keywords: species.search_keywords || [],
        popularity_score: species.popularity_score || 0,
        image_url: species.image_url || '',
        thumbnail_url: species.thumbnail_url || '',
        is_active: species.is_active !== undefined ? species.is_active : true
      });

      // Load related data
      if (species.id) {
        const loadRelatedData = async (speciesId) => {
          setLoadingData(true);
          try {
            // Load regional presence
            const presenceResponse = await speciesCatalogAdminApi.getRegionalPresence(speciesId);
            setRegionalPresence(presenceResponse.data || []);

            // Load water types (fish only)
            if (speciesType === 'fish') {
              const waterTypesResponse = await speciesCatalogAdminApi.getWaterTypes(speciesId);
              setWaterTypes(waterTypesResponse.data || []);
            }
          } catch (err) {
            console.error('Failed to load related data:', err);
          } finally {
            setLoadingData(false);
          }
        };
        loadRelatedData(species.id);
      }
    }
  }, [species, isCreating]);

  /**
   * Handle form field change
   */
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  /**
   * Validate form
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.common_name || formData.common_name.trim() === '') {
      newErrors.common_name = 'Common name is required';
    }

    if (formData.common_name && formData.common_name.length > 200) {
      newErrors.common_name = 'Common name must be 200 characters or less';
    }

    if (!formData.species_type) {
      newErrors.species_type = 'Species type is required';
    }

    if (formData.typical_size_min && formData.typical_size_max) {
      const min = parseFloat(formData.typical_size_min);
      const max = parseFloat(formData.typical_size_max);
      if (!isNaN(min) && !isNaN(max) && min > max) {
        newErrors.typical_size_max = 'Max size must be greater than or equal to min size';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle save
   */
  const handleSave = async () => {
    if (!validateForm()) {
      setActiveTab('basic'); // Switch to basic tab to show errors
      return;
    }

    setIsSaving(true);
    try {
      await onSave(formData);
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Save failed:', err);
      setErrors({ _general: err.message || 'Failed to save species' });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle close with unsaved changes warning
   */
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setConfirmModal({
        isOpen: true,
        action: 'close',
        data: null,
        message: 'You have unsaved changes. Are you sure you want to close?'
      });
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  /**
   * Handle escape key
   */
  useEffect(() => {
    const handleEscape = (  _e) => {
      if (_e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [handleClose]);

  /**
   * Prevent background scroll
   */
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // ========================================================================
  // REGIONAL PRESENCE HANDLERS
  // ========================================================================

  /**
   * Handle add regional presence
   */
  const handleAddRegionalPresence = () => {
    setPresenceForm({
      state_code: '',
      presence_type: 'native',
      abundance: 'common',
      peak_season_start: null,
      peak_season_end: null
    });
    setEditingPresence({ isNew: true });
  };

  /**
   * Handle edit regional presence
   */
  const handleEditRegionalPresence = (presence) => {
    setPresenceForm({ ...presence });
    setEditingPresence(presence);
  };

  /**
   * Handle save regional presence
   */
  const handleSaveRegionalPresence = async () => {
    if (!presenceForm.state_code) {
      setErrors(prev => ({ ...prev, _general: 'Please select a state' }));
      setTimeout(() => setErrors(prev => ({ ...prev, _general: null })), 3000);
      return;
    }

    try {
      if (editingPresence.isNew) {
        // Add new
        const response = await speciesCatalogAdminApi.addRegionalPresence(species.id, presenceForm);
        setRegionalPresence(prev => [...prev, response.data]);
      } else {
        // Update existing
        const response = await speciesCatalogAdminApi.updateRegionalPresence(
          species.id,
          editingPresence.id,
          presenceForm
        );
        setRegionalPresence(prev =>
          prev.map(p => p.id === editingPresence.id ? response.data : p)
        );
      }
      setEditingPresence(null);
      setPresenceForm({});
    } catch (err) {
      console.error('Failed to save regional presence:', err);
      setErrors(prev => ({ ...prev, _general: 'Failed to save regional presence: ' + err.message }));
      setTimeout(() => setErrors(prev => ({ ...prev, _general: null })), 3000);
    }
  };

  /**
   * Handle delete regional presence
   */
  const handleDeleteRegionalPresence = async (presenceId) => {
    setConfirmModal({
      isOpen: true,
      action: 'deletePresence',
      data: presenceId,
      message: 'Delete this regional presence record?'
    });
  };

  const executeDeleteRegionalPresence = async (presenceId) => {

    try {
      await speciesCatalogAdminApi.deleteRegionalPresence(species.id, presenceId);
      setRegionalPresence(prev => prev.filter(p => p.id !== presenceId));
    } catch (err) {
      console.error('Failed to delete regional presence:', err);
      setErrors(prev => ({ ...prev, _general: 'Failed to delete: ' + err.message }));
      setTimeout(() => setErrors(prev => ({ ...prev, _general: null })), 3000);
    }
  };

  /**
   * Handle cancel regional presence edit
   */
  const handleCancelRegionalPresence = () => {
    setEditingPresence(null);
    setPresenceForm({});
  };

  // ========================================================================
  // WATER TYPES HANDLERS
  // ========================================================================

  /**
   * Handle add water type
   */
  const handleAddWaterType = () => {
    setWaterTypeForm({ water_type: 'freshwater', water_body_type: '' });
    setAddingWaterType(true);
  };

  /**
   * Handle save water type
   */
  const handleSaveWaterType = async () => {
    if (!waterTypeForm.water_type) {
      setErrors(prev => ({ ...prev, _general: 'Please select a water type' }));
      setTimeout(() => setErrors(prev => ({ ...prev, _general: null })), 3000);
      return;
    }

    try {
      const response = await speciesCatalogAdminApi.addWaterType(species.id, waterTypeForm);
      setWaterTypes(prev => [...prev, response.data]);
      setAddingWaterType(false);
      setWaterTypeForm({ water_type: '', water_body_type: '' });
    } catch (err) {
      console.error('Failed to add water type:', err);
      setErrors(prev => ({ ...prev, _general: 'Failed to add water type: ' + err.message }));
      setTimeout(() => setErrors(prev => ({ ...prev, _general: null })), 3000);
    }
  };

  /**
   * Handle delete water type
   */
  const handleDeleteWaterType = async (waterType) => {
    setConfirmModal({
      isOpen: true,
      action: 'deleteWaterType',
      data: waterType,
      message: 'Delete this water type?'
    });
  };

  const executeDeleteWaterType = async (waterType) => {

    try {
      await speciesCatalogAdminApi.deleteWaterType(
        species.id,
        waterType.water_type,
        waterType.water_body_type
      );
      setWaterTypes(prev =>
        prev.filter(wt =>
          !(wt.water_type === waterType.water_type && wt.water_body_type === waterType.water_body_type)
        )
      );
    } catch (err) {
      console.error('Failed to delete water type:', err);
      setErrors(prev => ({ ...prev, _general: 'Failed to delete: ' + err.message }));
      setTimeout(() => setErrors(prev => ({ ...prev, _general: null })), 3000);
    }
  };

  /**
   * Handle cancel water type
   */
  const handleCancelWaterType = () => {
    setAddingWaterType(false);
    setWaterTypeForm({ water_type: '', water_body_type: '' });
  };

  // ========================================================================
  // IMAGE UPLOAD HANDLERS
  // ========================================================================

  /**
   * Handle image file selection
   */
  const handleImageUpload = async (  _e) => {
    const file = _e.target.files[0];
    if (!file) {return;}

    // Validate file
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, _general: 'File too large. Maximum size: 5MB' }));
      setTimeout(() => setErrors(prev => ({ ...prev, _general: null })), 3000);
      return;
    }

    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      setErrors(prev => ({ ...prev, _general: 'Invalid file type. Allowed: JPG, PNG, WebP' }));
      setTimeout(() => setErrors(prev => ({ ...prev, _general: null })), 3000);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress (real progress would require XMLHttpRequest)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await speciesCatalogAdminApi.uploadImage(species.id, file);

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Update form data with new URLs
      setFormData(prev => ({
        ...prev,
        image_url: response.data.imageUrl,
        thumbnail_url: response.data.thumbnailUrl
      }));
      setHasUnsavedChanges(true);

      setTimeout(() => {
        setUploadProgress(0);
        setUploading(false);
      }, 1000);
    } catch (err) {
      console.error('Image upload failed:', err);
      setErrors(prev => ({ ...prev, _general: 'Image upload failed: ' + err.message }));
      setTimeout(() => setErrors(prev => ({ ...prev, _general: null })), 3000);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {isCreating ? 'Create New Species' : `Edit ${formData.common_name || 'Species'}`}
              </h2>
              {!isCreating && species?.id && (
                <p className="text-sm text-gray-500 mt-1">ID: {species.id}</p>
              )}
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 px-6">
            <button
              onClick={() => setActiveTab('basic')}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'basic'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              Basic Info
            </button>
            {!isCreating && (
              <>
                <button
                  onClick={() => setActiveTab('regional')}
                  className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'regional'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  Regional Presence ({regionalPresence.length})
                </button>
                {formData.species_type === 'fish' && (
                  <button
                    onClick={() => setActiveTab('water')}
                    className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'water'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    }`}
                  >
                    Water Types ({waterTypes.length})
                  </button>
                )}
                <button
                  onClick={() => setActiveTab('media')}
                  className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'media'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  Media
                </button>
              </>
            )}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'basic' && (
              <div className="space-y-6 max-w-4xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    label="Common Name"
                    name="common_name"
                    value={formData.common_name}
                    onChange={(val) => handleFieldChange('common_name', val)}
                    error={errors.common_name}
                    required
                    placeholder="e.g., Rainbow Trout"
                  />

                  <FormField
                    label="Scientific Name"
                    name="scientific_name"
                    value={formData.scientific_name}
                    onChange={(val) => handleFieldChange('scientific_name', val)}
                    placeholder="e.g., Oncorhynchus mykiss"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    label="Species Type"
                    name="species_type"
                    type="select"
                    value={formData.species_type}
                    onChange={(val) => {
                      handleFieldChange('species_type', val);
                      handleFieldChange('category', ''); // Reset category when type changes
                    }}
                    error={errors.species_type}
                    required
                    options={[
                      { value: 'fish', label: 'Fish' },
                      { value: 'game', label: 'Game' },
                      { value: 'wildlife', label: 'Wildlife' }
                    ]}
                  />

                  <FormField
                    label="Category"
                    name="category"
                    type="select"
                    value={formData.category}
                    onChange={(val) => handleFieldChange('category', val)}
                    options={[
                      { value: '', label: 'Select category...' },
                      ...(CATEGORY_OPTIONS[formData.species_type] || []).map(cat => ({
                        value: cat,
                        label: cat
                      }))
                    ]}
                  />
                </div>

                <FormField
                  label="Default Description"
                  name="default_description"
                  type="textarea"
                  value={formData.default_description}
                  onChange={(val) => handleFieldChange('default_description', val)}
                  rows={5}
                  helperText="Used when guides don't provide a custom description"
                  placeholder="Describe the species, habitat, behavior, etc."
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <FormField
                    label="Typical Size Min"
                    name="typical_size_min"
                    value={formData.typical_size_min}
                    onChange={(val) => handleFieldChange('typical_size_min', val)}
                    placeholder="e.g., 12"
                  />

                  <FormField
                    label="Typical Size Max"
                    name="typical_size_max"
                    value={formData.typical_size_max}
                    onChange={(val) => handleFieldChange('typical_size_max', val)}
                    error={errors.typical_size_max}
                    placeholder="e.g., 24"
                  />

                  <FormField
                    label="Size Unit"
                    name="size_unit"
                    type="select"
                    value={formData.size_unit}
                    onChange={(val) => handleFieldChange('size_unit', val)}
                    options={[
                      { value: 'inches', label: 'Inches' },
                      { value: 'pounds', label: 'Pounds' },
                      { value: 'feet', label: 'Feet' }
                    ]}
                  />
                </div>

                <FormField
                  label="Search Keywords"
                  name="search_keywords"
                  value={formData.search_keywords.join(', ')}
                  onChange={(val) => handleFieldChange('search_keywords', val.split(',').map(k => k.trim()).filter(Boolean))}
                  helperText="Comma-separated aliases (e.g., 'steelhead, rainbow')"
                  placeholder="Alternative names, nicknames, etc."
                />

                <FormField
                  label="Popularity Score"
                  name="popularity_score"
                  type="number"
                  value={formData.popularity_score}
                  onChange={(val) => handleFieldChange('popularity_score', parseInt(val) || 0)}
                  min={0}
                  max={100}
                  helperText="Higher values feature this species more prominently (0-100)"
                />
              </div>
            )}

            {activeTab === 'regional' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    Regional Presence by State
                  </h3>
                  <button
                    onClick={handleAddRegionalPresence}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add State
                  </button>
                </div>

                {loadingData ? (
                  <div className="text-center py-8">
                    <Loader className="w-8 h-8 animate-spin mx-auto text-blue-600" />
                    <p className="mt-2 text-gray-600">Loading regional data...</p>
                  </div>
                ) : regionalPresence.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-gray-600">No regional presence records yet.</p>
                    <p className="text-sm text-gray-500 mt-1">Add states where this species is found.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Presence Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Abundance</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Peak Season</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {regionalPresence.map((rp) => (
                          <tr key={rp.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {rp.state_code}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {rp.presence_type}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {rp.abundance}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {rp.peak_season_start && rp.peak_season_end
                                ? `${MONTHS[rp.peak_season_start - 1]} - ${MONTHS[rp.peak_season_end - 1]}`
                                : 'N/A'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                              <button
                                onClick={() => handleEditRegionalPresence(rp)}
                                className="text-blue-600 hover:text-blue-900 mr-3"
                              >
                                <Edit className="w-4 h-4 inline" />
                              </button>
                              <button
                                onClick={() => handleDeleteRegionalPresence(rp.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <Trash className="w-4 h-4 inline" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Regional Presence Form Modal */}
                {editingPresence && (
                  <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={handleCancelRegionalPresence} />
                    <div className="flex min-h-full items-center justify-center p-4">
                      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                          {editingPresence.isNew ? 'Add' : 'Edit'} Regional Presence
                        </h3>
                        <div className="space-y-4">
                          <FormField
                            label="State"
                            name="state_code"
                            type="select"
                            value={presenceForm.state_code}
                            onChange={(val) => setPresenceForm(prev => ({ ...prev, state_code: val }))}
                            required
                            options={[
                              { value: '', label: 'Select state...' },
                              ...US_STATES.map(s => ({ value: s.code, label: s.name }))
                            ]}
                          />

                          <FormField
                            label="Presence Type"
                            name="presence_type"
                            type="select"
                            value={presenceForm.presence_type}
                            onChange={(val) => setPresenceForm(prev => ({ ...prev, presence_type: val }))}
                            options={[
                              { value: 'native', label: 'Native' },
                              { value: 'introduced', label: 'Introduced' },
                              { value: 'stocked', label: 'Stocked' },
                              { value: 'invasive', label: 'Invasive' },
                              { value: 'occasional', label: 'Occasional' },
                              { value: 'migratory', label: 'Migratory' }
                            ]}
                          />

                          <FormField
                            label="Abundance"
                            name="abundance"
                            type="select"
                            value={presenceForm.abundance}
                            onChange={(val) => setPresenceForm(prev => ({ ...prev, abundance: val }))}
                            options={[
                              { value: 'abundant', label: 'Abundant' },
                              { value: 'common', label: 'Common' },
                              { value: 'uncommon', label: 'Uncommon' },
                              { value: 'rare', label: 'Rare' }
                            ]}
                          />

                          <div className="grid grid-cols-2 gap-4">
                            <FormField
                              label="Peak Season Start"
                              name="peak_season_start"
                              type="select"
                              value={presenceForm.peak_season_start || ''}
                              onChange={(val) => setPresenceForm(prev => ({ ...prev, peak_season_start: val ? parseInt(val) : null }))}
                              options={[
                                { value: '', label: 'None' },
                                ...MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))
                              ]}
                            />

                            <FormField
                              label="Peak Season End"
                              name="peak_season_end"
                              type="select"
                              value={presenceForm.peak_season_end || ''}
                              onChange={(val) => setPresenceForm(prev => ({ ...prev, peak_season_end: val ? parseInt(val) : null }))}
                              options={[
                                { value: '', label: 'None' },
                                ...MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))
                              ]}
                            />
                          </div>
                        </div>

                        <div className="mt-6 flex gap-3">
                          <button
                            onClick={handleCancelRegionalPresence}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveRegionalPresence}
                            className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'water' && formData.species_type === 'fish' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    Water Body Types
                  </h3>
                  <button
                    onClick={handleAddWaterType}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Water Type
                  </button>
                </div>

                {loadingData ? (
                  <div className="text-center py-8">
                    <Loader className="w-8 h-8 animate-spin mx-auto text-blue-600" />
                    <p className="mt-2 text-gray-600">Loading water types...</p>
                  </div>
                ) : waterTypes.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                    <Fish className="w-12 h-12 mx-auto text-gray-300" />
                    <p className="mt-4 text-gray-600">No water types defined yet.</p>
                    <p className="text-sm text-gray-500">Add the types of water bodies where this fish is found.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {waterTypes.map((wt, index) => (
                      <div
                        key={index}
                        className="border rounded-lg p-4 flex justify-between items-start hover:border-blue-300 transition-colors"
                      >
                        <div>
                          <span className="font-medium text-gray-900 capitalize">{wt.water_type}</span>
                          {wt.water_body_type && (
                            <span className="text-gray-600"> - {wt.water_body_type}</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteWaterType(wt)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Water Type Form Modal */}
                {addingWaterType && (
                  <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={handleCancelWaterType} />
                    <div className="flex min-h-full items-center justify-center p-4">
                      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Add Water Type</h3>
                        <div className="space-y-4">
                          <FormField
                            label="Water Type"
                            name="water_type"
                            type="select"
                            value={waterTypeForm.water_type}
                            onChange={(val) => setWaterTypeForm(prev => ({ ...prev, water_type: val }))}
                            required
                            options={[
                              { value: '', label: 'Select type...' },
                              { value: 'freshwater', label: 'Freshwater' },
                              { value: 'saltwater', label: 'Saltwater' },
                              { value: 'brackish', label: 'Brackish' }
                            ]}
                          />

                          <FormField
                            label="Water Body Type"
                            name="water_body_type"
                            type="select"
                            value={waterTypeForm.water_body_type}
                            onChange={(val) => setWaterTypeForm(prev => ({ ...prev, water_body_type: val }))}
                            options={[
                              { value: '', label: 'Optional...' },
                              { value: 'river', label: 'River' },
                              { value: 'stream', label: 'Stream' },
                              { value: 'lake', label: 'Lake' },
                              { value: 'pond', label: 'Pond' },
                              { value: 'reservoir', label: 'Reservoir' },
                              { value: 'ocean', label: 'Ocean' },
                              { value: 'bay', label: 'Bay' },
                              { value: 'estuary', label: 'Estuary' },
                              { value: 'creek', label: 'Creek' },
                              { value: 'tailwater', label: 'Tailwater' }
                            ]}
                          />
                        </div>

                        <div className="mt-6 flex gap-3">
                          <button
                            onClick={handleCancelWaterType}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveWaterType}
                            className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'media' && (
              <div className="space-y-8 max-w-2xl">
                {/* Current Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Current Image
                  </label>
                  {formData.image_url ? (
                    <div className="relative inline-block">
                      <img
                        src={formData.image_url}
                        alt={formData.common_name || 'Species'}
                        className="w-64 h-64 object-cover rounded-lg border border-gray-300 shadow-sm"
                      />
                      <button
                        onClick={() => {
                          handleFieldChange('image_url', '');
                          handleFieldChange('thumbnail_url', '');
                        }}
                        className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 transition-colors"
                        title="Remove image"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-64 h-64 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                      <div className="text-center">
                        <ImageIcon className="w-16 h-16 text-gray-400 mx-auto" />
                        <p className="mt-2 text-sm text-gray-500">No image uploaded</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Upload New Image */}
                {!isCreating && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Upload New Image
                    </label>
                    <div className="flex items-center gap-4">
                      <label className="relative cursor-pointer bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                        <Upload className="w-4 h-4 inline mr-2" />
                        Choose File
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleImageUpload}
                          disabled={uploading}
                          className="sr-only"
                        />
                      </label>
                      {uploading && <Loader className="w-5 h-5 animate-spin text-blue-600" />}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Max 5MB. Formats: JPG, PNG, WebP
                    </p>
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="mt-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <p className="mt-1 text-xs text-gray-600">{uploadProgress}% uploaded</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Manual URL Entry */}
                <div>
                  <FormField
                    label="Image URL"
                    name="image_url"
                    type="url"
                    value={formData.image_url}
                    onChange={(val) => handleFieldChange('image_url', val)}
                    placeholder="https://..."
                    helperText="Enter URL if not uploading a file"
                  />
                </div>

                <div>
                  <FormField
                    label="Thumbnail URL"
                    name="thumbnail_url"
                    type="url"
                    value={formData.thumbnail_url}
                    onChange={(val) => handleFieldChange('thumbnail_url', val)}
                    placeholder="https://..."
                    helperText="Auto-generated on upload, or enter manually"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200">
            <div className="flex items-center gap-2">
              {hasUnsavedChanges && (
                <span className="text-sm text-amber-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  Unsaved changes
                </span>
              )}
              {errors._general && (
                <span className="text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors._general}
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                disabled={isSaving}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !hasUnsavedChanges}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {isSaving ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {isCreating ? 'Create Species' : 'Save Changes'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, action: null, data: null, message: '' })}
        onConfirm={async () => {
          const { action, data } = confirmModal;
          setConfirmModal({ isOpen: false, action: null, data: null, message: '' });

          if (action === 'close') {
            onClose();
          } else if (action === 'deletePresence') {
            await executeDeleteRegionalPresence(data);
          } else if (action === 'deleteWaterType') {
            await executeDeleteWaterType(data);
          }
        }}
        title="Confirm Action"
        message={confirmModal.message}
        confirmText="Confirm"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
}
